import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Avatar,
    Divider,
} from '@mui/material';
import {
    ExpandMore,
    TrendingUp,
    TrendingDown,
} from '@mui/icons-material';
import { ComparisonCandidate, TestResult } from '../types';
import { ResultSection } from './ResultSection';
import { getScoreColor } from '../utils';

interface CandidateAccordionProps {
    candidate: ComparisonCandidate;
    expanded: boolean;
    onChange: () => void;
    newResult?: TestResult;
    oldResult?: TestResult;
    compareWithActive: boolean;
    scoreDiff: number;
}

export const CandidateAccordion: React.FC<CandidateAccordionProps> = ({
    candidate,
    expanded,
    onChange,
    newResult,
    oldResult,
    compareWithActive,
    scoreDiff,
}) => {
    return (
        <Accordion
            expanded={expanded}
            onChange={onChange}
            sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:before': { display: 'none' } }}
        >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={candidate.avatar} sx={{ width: 40, height: 40 }}>
                            {candidate.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box>
                            <Typography variant="body1" fontWeight={600}>
                                {candidate.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {candidate.title}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {compareWithActive && oldResult && oldResult.score > 0 && (
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Parent
                                </Typography>
                                <Typography variant="h6" sx={{ color: getScoreColor(oldResult.score) }}>
                                    {Math.round(oldResult.score * 100)}%
                                </Typography>
                            </Box>
                        )}

                        {compareWithActive && oldResult && newResult && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    bgcolor: scoreDiff > 0
                                        ? 'rgba(76, 175, 80, 0.1)'
                                        : scoreDiff < 0
                                            ? 'rgba(244, 67, 54, 0.1)'
                                            : 'rgba(0, 0, 0, 0.05)',
                                    color: scoreDiff > 0
                                        ? 'success.main'
                                        : scoreDiff < 0
                                            ? 'error.main'
                                            : 'text.secondary',
                                }}
                            >
                                {scoreDiff > 0 && <TrendingUp sx={{ fontSize: 16 }} />}
                                {scoreDiff < 0 && <TrendingDown sx={{ fontSize: 16 }} />}
                                <Typography variant="body2" fontWeight={600}>
                                    {scoreDiff === 0 ? 'No change' : `${scoreDiff > 0 ? '+' : ''}${scoreDiff}%`}
                                </Typography>
                            </Box>
                        )}

                        {newResult && newResult.score > 0 && (
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary">
                                    New
                                </Typography>
                                <Typography variant="h6" sx={{ color: getScoreColor(newResult.score) }}>
                                    {Math.round(newResult.score * 100)}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2 }}>
                <Divider sx={{ my: 1.5 }} />
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: compareWithActive ? '1fr 1fr' : '1fr',
                        gap: 3,
                    }}
                >
                    {compareWithActive && oldResult && <ResultSection result={oldResult} variant="old" />}
                    {newResult && <ResultSection result={newResult} variant="new" />}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
